import { type GenericMap, type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { EMPTY_ARRAY } from "@common/lib/consts";
import { usePromise } from "@common/lib/hooks";
import { parseQueryString } from "@common/lib/url";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import { type Api } from "@common/modules/system/types";
import React from "react";
import { collapseReportParameters, getReportBadges } from "../../lib";
import { type BadgeInfo, type DrillDownInfo, type ReportBreadcrumb } from "../../types";

export type StaticProviderReportData = {
  parameters: GenericMap<any>;
  getBadges: (parameters: GenericMap<any>, filters: Data.Api.Filter[], onBadgesClick: () => void) => BadgeInfo[];
  getFilters: (id: string, values: Api.GetFormFieldValue[], options?: OptionItem<any>[]) => Promise<Data.Api.Filter[]>;
  breadcrumbs?: ReportBreadcrumb[];
  fields: Data.Api.FormField[];
  onDrillDown: (value: any, label: string, parameters: GenericMap<any>) => void;
  goTo: (reportId: string) => void;
  drillDownInfo: DrillDownInfo[];
};

export type Props = {
  badges: string[];
  category: string;
  children: (data: StaticProviderReportData) => any;
  drillDownInfo?: Omit<DrillDownInfo, "valueLabel" | "value">;
  fields: Data.Api.FormField[];
  onLoaded?: () => void;
  isLoading?: boolean;
};

const CommonStaticProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const [parameters, setParameters] = React.useState(collapseReportParameters(parseQueryString()));
  const [drillDownInfo, setDrillDownInfo] = React.useState<DrillDownInfo[]>([]);

  const [reportCategories, isReportCategoriesLoading] = usePromise<Data.Api.ReportCategory[]>({
    fn: api.getCategories,
    args: [],
    shouldLoad: true
  });

  const getBadges = React.useCallback(
    (parameters: GenericMap<any>, filters: Data.Api.Filter[], onBadgeClick: () => void): BadgeInfo[] =>
      getReportBadges(i18n, {
        badges: props.badges,
        fields: props.fields,
        parameters: parameters,
        drillDownInfo: EMPTY_ARRAY,
        filters: filters,
        onBadgeClick: onBadgeClick
      }),
    [i18n, props.fields, props.badges]
  );

  const getFilters = React.useCallback(
    (values: Api.GetFormFieldValue[], options?: OptionItem<any>[]): Data.Api.Filter[] =>
      props.fields
        .filter((x) => values.some((y) => y.formFieldId === x.formFieldId))
        .map((x) => {
          const requestedValues = values
            .find((z) => z.formFieldId === x.formFieldId)!
            .value!.toString()
            .split(",");
          return {
            key: x.formFieldKey!,
            label: x.label!,
            displayLabel: null,
            displayName: (options ?? [])
              .filter((y) => requestedValues.includes(y.value))
              .map((y) => y.label)
              .toString()
          };
        }),
    [props.fields]
  );

  const goTo = React.useCallback(
    (reportId: string) => {
      if (!drillDownInfo.length) throw new Error("BUG");

      setDrillDownInfo([]);
      const params = { ...parameters };
      delete params[props.drillDownInfo!.key];
      setParameters(params);
    },
    [drillDownInfo, parameters]
  );

  // TODO: modify if need several drillDown levels, now supporting only one level
  const onDrillDown = React.useCallback(
    (value: any, label: string, parameters: GenericMap<any>) => {
      if (drillDownInfo.length || !props.drillDownInfo) return;

      setDrillDownInfo([{ ...props.drillDownInfo, value, valueLabel: label }]);

      setParameters(parameters);
    },
    [drillDownInfo, props.drillDownInfo]
  );

  const breadcrumbs = React.useMemo<ReportBreadcrumb[]>(
    () =>
      drillDownInfo.map((x) => ({
        reportId: x.reportId,
        reportName: x.reportName
      })),
    [drillDownInfo]
  );

  React.useEffect(() => {
    !props.isLoading && !isReportCategoriesLoading && props.onLoaded && props.onLoaded();
  }, [props.isLoading, isReportCategoriesLoading, props.onLoaded]);

  if (
    props.isLoading ||
    isReportCategoriesLoading ||
    !reportCategories?.map((x) => x.reportCategoryKey).includes(props.category)
  )
    return null;

  return props.children({
    parameters,
    getBadges,
    getFilters: (id, values, options) => Promise.resolve(getFilters(values, options)),
    breadcrumbs,
    fields: props.fields,
    onDrillDown,
    goTo,
    drillDownInfo
  });
};

export default CommonStaticProvider;
